import React, { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faArrowDownShortWide, faArrowUpShortWide } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown } from "antd";
import { defineMessages, useIntl } from "react-intl";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import User from "@mapmycustomers/shared/types/User";
import { Button, SelectField } from "@mapmycustomers/ui";

import LocationPeriod, {
  getDaysOffsetByLocationPeriod,
  getUserLocationPeriodDisplayName,
} from "@app/scene/map/components/layers/UserLocation/enum/LocationPeriod";
import { fetchLocations } from "@app/scene/map/store/actions";
import { getCurrentUser } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import formatDateRange from "@app/util/stackRank/formatDateRange";

import getSortDirectionLabel from "../../util/getSortDirectionLabel";

import CustomRangePicker from "./CustomRangePicker";
import styles from "./LocationFilters.module.scss";

export const messages = defineMessages({
  apply: {
    id: "map.layer.userLocation.dateRange.custom.apply",
    defaultMessage: "Apply",
    description: "Activities list sort select apply",
  },
  cancel: {
    id: "map.layer.userLocation.dateRange.custom.cancel",
    defaultMessage: "Cancel",
    description: "Activities list sort select cancel",
  },
  change: {
    id: "map.layer.userLocation.dateRange.change",
    defaultMessage: "Change",
    description: "Change custom date range",
  },
  changeDateRange: {
    id: "map.layer.userLocation.dateRange.changeDateRange",
    defaultMessage: "Change date range",
    description: "Reports date range selector",
  },
  selectRange: {
    id: "map.layer.userLocation.dateRange.select",
    defaultMessage: "Select range",
    description: "Reports custom selector select label",
  },
});

export interface LocationFiltersProps {
  onFetchLocations: typeof fetchLocations.request;
  user: User;
}

const LocationFilters: FC<LocationFiltersProps> = ({ onFetchLocations, user }) => {
  const intl = useIntl();
  const today = new Date();
  const [period, setPeriod] = useState<LocationPeriod>(LocationPeriod.LAST_24_HOURS);
  const [sort, setSort] = useState<SortOrder>(SortOrder.ASC);
  const [dateRange, setNewDateRange] = useState<DateRange>({ endDate: today, startDate: today });

  useEffect(() => {
    onFetchLocations({
      dateRange: period === LocationPeriod.CUSTOM ? dateRange : undefined,
      daysOffset: getDaysOffsetByLocationPeriod(period),
      sort,
    });
  }, [dateRange, onFetchLocations, period, sort, user]);

  const options = useMemo(
    () =>
      [
        LocationPeriod.LAST_24_HOURS,
        LocationPeriod.LAST_48_HOURS,
        LocationPeriod.LAST_7_DAYS,
        LocationPeriod.LAST_30_DAYS,
        LocationPeriod.LAST_60_DAYS,
        LocationPeriod.LAST_90_DAYS,
        LocationPeriod.CUSTOM,
      ].map((period) => ({ label: getUserLocationPeriodDisplayName(intl, period), value: period })),
    [intl]
  );

  const items = useMemo(
    () => [
      {
        key: SortOrder.ASC,
        label: getSortDirectionLabel(intl, SortOrder.ASC),
        onClick: () => setSort(SortOrder.ASC),
      },
      {
        key: SortOrder.DESC,
        label: getSortDirectionLabel(intl, SortOrder.DESC),
        onClick: () => setSort(SortOrder.DESC),
      },
    ],
    [intl]
  );

  return (
    <div className={styles.filters}>
      <div className={styles.period}>
        <SelectField
          className={styles.periodSelector}
          onSelect={setPeriod}
          options={options}
          size="middle"
          value={period}
        />
        {period === LocationPeriod.CUSTOM && (
          <div className={styles.customRange}>
            <div>{formatDateRange(dateRange, "PP")}</div>
            <CustomRangePicker dateRange={dateRange} onChange={setNewDateRange} />
          </div>
        )}
      </div>
      <Dropdown menu={{ items, selectedKeys: [sort] }}>
        <Button
          leftIcon={
            <FontAwesomeIcon
              icon={sort === SortOrder.ASC ? faArrowDownShortWide : faArrowUpShortWide}
            />
          }
        >
          {getSortDirectionLabel(intl, sort)}
        </Button>
      </Dropdown>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  user: getCurrentUser(state)!,
});

const mapDispatchToProps = {
  onFetchLocations: fetchLocations.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(LocationFilters);
